import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery,
  Badge,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as WillsIcon,
  AccountBalance as AssetsIcon,
  Security as VaultIcon,
  Logout as LogoutIcon,
  Build as BuildIcon,

  AccountTree as UserListIcon,
  NotificationsActive as AlarmIcon,
  PersonAdd as InviteIcon,
  Quiz as QuizIcon,
  RateReview as FeedbackIcon,
  Description as TemplateIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { IQuestionnaireExpiredAssignment } from '../types/questionnaire';
import { stitchColors } from '../theme/tokens';
import { isDraftWorkflowRoute } from '../utils/draftWorkflow';

const drawerWidth = 256;

const SUBSCRIPTION_LABELS: Record<string, string> = {
  FREE: 'Free',
  PRO: 'Pro',
  PLUS: 'Plus',
};

const USER_TYPE_LABELS: Record<string, string> = {
  EO: 'Estate Owner',
  EL: 'Estate Lawyer',
  EX: 'Estate Executor',
  EW: 'Estate Witness',
};

const navLabelTextShadow = '0 1px 2px rgba(18, 28, 40, 0.14)';
const navLabelTextShadowOnDark = '0 1px 2px rgba(0, 0, 0, 0.28)';
const topBarUserTypeTextShadow = '0 1px 3px rgba(18, 28, 40, 0.28), 0 2px 6px rgba(18, 28, 40, 0.16)';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPendingQuestionnaires } = useAuthStore();
  const canUseDocumentWorkflow = ['EO', 'EL', 'EX'].includes(user?.userType || '');

  const { data: assignments = [] } = useQuery<IQuestionnaireExpiredAssignment[]>({
    queryKey: ['expiredAssignments', user?.id],
    queryFn: async () => {
      const response = await api.get<IQuestionnaireExpiredAssignment[]>('/documents/expired-assignments');
      return response.data;
    },
    enabled: !!user && user.role === 'PLUS',
  });

  const { lastSeenAlarm } = useAuthStore();
  const expiredCount = assignments.filter(a => new Date(a.expiresAt).getTime() > lastSeenAlarm).length;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/',
      available: true
    },
    {
      text: 'Document Builder',
      icon: <BuildIcon />,
      path: '/document-builder',
      available: user?.userType === 'EO' && ['FREE', 'PRO', 'PLUS'].includes(user?.role || '')
    },
    {
      text: 'Document History',
      icon: <WillsIcon />,
      path: '/document-library',
      available: canUseDocumentWorkflow && user?.userType !== 'EX'
    },
    {
      text: 'Document Templates',
      icon: <TemplateIcon />,
      path: '/estate-lawyer/document-templates',
      available: user?.userType === 'EL'
    },
    {
      text: 'Welcome Questionnaires',
      icon: <QuizIcon />,
      path: '/questionnaire-library',
      available: user?.userType === 'EL' && ['PRO', 'PLUS'].includes(user?.role || '')
    },
    {
      text: 'Lawyer Management',
      icon: <InviteIcon />,
      path: '/lawyer-management',
      available: user?.userType === 'EO'
    },
    {
      text: 'Assigned Documents',
      icon: <WillsIcon />,
      path: '/assigned-documents',
      available: canUseDocumentWorkflow
    },
    {
      text: 'Alarm',
      icon: (
        <Badge badgeContent={expiredCount} color="error" overlap="circular">
          <AlarmIcon />
        </Badge>
      ),
      path: '/assigned-documents/alarms',
      available: user?.role === 'PLUS'
    },

    {
      text: 'Users',
      icon: <UserListIcon />,
      path: '/user-lists',
      available: true
    },
    {
      text: 'Assets',
      icon: <AssetsIcon />,
      path: '/assets',
      available: ['PRO', 'PLUS'].includes(user?.role || '')
    },
    {
      text: 'Invitation Links',
      icon: <InviteIcon />,
      path: '/invitation-links',
      available: user?.userType === 'EL' || user?.userType === 'EX' || user?.userType === 'EW'
    },
    {
      text: 'Questionnaire Config',
      icon: <BuildIcon />,
      path: '/admin/welcome-questionnaire',
      available: user?.role === 'PLUS'
    },
    {
      text: 'Personal Questions',
      icon: <QuizIcon />,
      path: '/personal-questionnaires',
      available: true
    },
    {
      text: 'Key Vault',
      icon: <VaultIcon />,
      path: '/keyvault',
      available: user?.role === 'PLUS'
    },
    {
      text: 'Feedback',
      icon: <FeedbackIcon />,
      path: '/feedback',
      available: true
    },
    {
      text: 'Feedback Review',
      icon: <FeedbackIcon />,
      path: '/admin/feedback',
      available: user?.role === 'PLUS'
    }
  ];

  const isExemptFromQuestionnaire =
    user?.subscription === 'PLUS' ||
    user?.userType === 'EL' ||
    user?.userType === 'EX' ||
    user?.userType === 'EW';

  const filteredMenuItems = menuItems.filter(item => item.available
    && (!hasPendingQuestionnaires || isExemptFromQuestionnaire || isDraftWorkflowRoute(item.path)));

  const subscriptionPlan = user?.subscription || user?.role || 'FREE';
  const subscriptionLabel = SUBSCRIPTION_LABELS[subscriptionPlan] || subscriptionPlan;
  const userTypeLabel = user?.userType ? USER_TYPE_LABELS[user.userType] || user.userType : '';

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: stitchColors.surfaceContainerLow,
        py: 3,
        px: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, px: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: stitchColors.primary,
            color: stitchColors.onPrimary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AssetsIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: theme.typography.h3.fontFamily,
              fontSize: '1.125rem',
              fontWeight: 800,
              color: stitchColors.onBackground,
              lineHeight: 1.2,
            }}
          >
            Australian Wills
          </Typography>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: stitchColors.onSurfaceVariant,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Estate Management
          </Typography>
          <Chip
            label={`${subscriptionLabel} plan`}
            size="small"
            sx={{
              mt: 1,
              height: 22,
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              bgcolor:
                subscriptionPlan === 'PLUS'
                  ? stitchColors.primary
                  : subscriptionPlan === 'PRO'
                    ? stitchColors.secondaryContainer
                    : stitchColors.surfaceContainerHighest,
              color:
                subscriptionPlan === 'PLUS'
                  ? stitchColors.onPrimary
                  : subscriptionPlan === 'PRO'
                    ? stitchColors.onSecondaryFixedVariant
                    : stitchColors.onSurfaceVariant,
              border:
                subscriptionPlan === 'FREE'
                  ? `1px solid ${stitchColors.outlineVariant}`
                  : 'none',
              '& .MuiChip-label': {
                textShadow:
                  subscriptionPlan === 'PLUS' ? navLabelTextShadowOnDark : navLabelTextShadow,
              },
            }}
          />
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 0.5, py: 0 }}>
        {filteredMenuItems.map((item) => {
          const isAssignedWillsV2Active = location.pathname === '/assigned-documents';
          const isAlarmActive = location.pathname === '/assigned-documents/alarms';
          const isActive = item.path === '/assigned-documents'
            ? isAssignedWillsV2Active
            : item.path === '/assigned-documents/alarms'
              ? isAlarmActive
              : location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 1,
                  minHeight: 44,
                  py: 1,
                  px: 1.5,
                  border: isActive ? `1px solid ${stitchColors.navBorder}` : '1px solid transparent',
                  bgcolor: isActive ? stitchColors.surfaceContainerLowest : 'transparent',
                  color: isActive ? stitchColors.onBackground : stitchColors.onSurfaceVariant,
                  boxShadow: isActive ? '0 1px 2px rgba(18, 28, 40, 0.06)' : 'none',
                  '&:hover': {
                    bgcolor: `${stitchColors.surfaceContainerLow} !important`,
                    pl: !isActive ? 1.75 : 1.5,
                  },
                  transition: 'padding 0.2s ease, background-color 0.2s ease',
                }}
              >
                <ListItemIcon
                  sx={{
                    color: 'inherit',
                    minWidth: 38,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontFamily: theme.typography.h3.fontFamily,
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: stitchColors.outlineVariant, my: 1 }} />

      <Box sx={{ px: 0.5 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            minHeight: 44,
            color: stitchColors.onSurfaceVariant,
            '&:hover': {
              bgcolor: stitchColors.errorContainer,
              color: stitchColors.error,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: stitchColors.surfaceContainerLowest,
          borderBottom: `1px solid ${stitchColors.navBorder}`,
        }}
      >
        <Toolbar sx={{ minHeight: 64, px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { md: 'none' },
              color: stitchColors.onBackground,
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            sx={{
              display: { xs: 'block', md: 'none' },
              fontFamily: theme.typography.h3.fontFamily,
              fontWeight: 800,
              fontSize: '1rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: stitchColors.onBackground,
            }}
          >
            Wills
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {userTypeLabel && (
              <Typography
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  fontSize: { sm: '0.9375rem', md: '1rem' },
                  fontWeight: 600,
                  color: stitchColors.onSurfaceVariant,
                  lineHeight: 1.2,
                  textShadow: topBarUserTypeTextShadow,
                }}
              >
                {userTypeLabel}
              </Typography>
            )}
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: stitchColors.surfaceTint,
                color: stitchColors.onPrimary,
                fontSize: '0.875rem',
                fontWeight: 600,
                border: `1px solid ${stitchColors.outlineVariant}`,
              }}
            >
              {user?.name?.charAt(0) || 'U'}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: stitchColors.surfaceContainerLow,
              border: 'none'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: stitchColors.surfaceContainerLow,
              border: 'none',
              boxShadow: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          backgroundColor: stitchColors.surfaceBright,
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
